import { DeepChat } from "deep-chat-react";
import { RequestDetails } from "deep-chat/dist/types/interceptors";
import { useRef, useState, useEffect } from "react";
import "./style.css";

const App = () => {
  const conversationIdRef = useRef<string | null>(null);
  const chatRef = useRef<any>(null);
  const [resetKey, setResetKey] = useState(0);
  const [googleChartsLoaded, setGoogleChartsLoaded] = useState(false);

  // Cargar la biblioteca de Google Charts cuando el componente se monta
  useEffect(() => {
    if (!document.getElementById("google-charts-script")) {
      const script = document.createElement("script");
      script.id = "google-charts-script";
      script.src = "https://www.gstatic.com/charts/loader.js";
      script.async = true;
      script.onload = () => {
        if (window.google && window.google.charts) {
          window.google.charts.load("current", { packages: ["corechart"] });
          window.google.charts.setOnLoadCallback(() => {
            setGoogleChartsLoaded(true);
            console.log("Google Charts cargado correctamente");
          });
        }
      };
      document.head.appendChild(script);
    } else if (window.google && window.google.charts) {
      setGoogleChartsLoaded(true);
    }
  }, []);

  // Registrar el componente personalizado google-chart
  useEffect(() => {
    if (googleChartsLoaded && !customElements.get("google-chart")) {
      class GoogleChart extends HTMLElement {
        private hasRendered = false;
        private chart: any = null;
        private dataTable: any = null;
        private resizeObserver: any = null;
        private chartOptions: any = null;

        connectedCallback() {
          if (this.hasRendered) return;
          this.hasRendered = true;

          while (this.firstChild) {
            this.removeChild(this.firstChild);
          }

          // Crear un contenedor con posición relativa para mejor control del tamaño
          const container = document.createElement("div");
          container.style.position = "relative";
          container.style.width = "100%";
          container.style.height = "100%";
          container.style.minHeight = "250px"; // Asegurar altura mínima
          container.style.overflow = "hidden";
          this.appendChild(container);

          // Crear el div para el gráfico con un ID único
          const chartDiv = document.createElement("div");
          chartDiv.id = "chart-" + Math.random().toString(36).substring(2, 15);
          chartDiv.style.position = "absolute";
          chartDiv.style.top = "0";
          chartDiv.style.left = "0";
          chartDiv.style.width = "100%";
          chartDiv.style.height = "100%";
          container.appendChild(chartDiv);

          // Obtener los atributos
          const data = JSON.parse(this.getAttribute("data") || "[]");
          const type = this.getAttribute("type") || "LineChart";
          const userOptions = JSON.parse(this.getAttribute("options") || "{}");

          // Configuración mejorada para responsividad
          this.chartOptions = {
            ...userOptions,
            width: "100%",
            height: "100%",
            chartArea: {
              width: "85%",
              height: "75%",
              left: "10%",
              top: "10%",
            },
            legend: {
              position: "top",
              alignment: "center",
              textStyle: {
                fontSize: 11,
                fontName: "Poppins",
              },
            },
            fontSize: 11,
            fontName: "Poppins", // Fuente global para el gráfico
            titleTextStyle: {
              fontName: "Poppins",
              fontSize: 14,
              bold: true,
            },
            animation: {
              duration: 500,
              easing: "out",
            },
            tooltip: {
              isHtml: true,
              textStyle: {
                fontName: "Poppins",
                fontSize: 11,
              },
            },
            hAxis: {
              ...userOptions.hAxis,
              textStyle: {
                fontSize: 10,
                fontName: "Poppins",
              },
              titleTextStyle: {
                fontName: "Poppins",
                fontSize: 11,
              },
              slantedText: true,
              slantedTextAngle: 45,
            },
            vAxis: {
              ...userOptions.vAxis,
              textStyle: {
                fontSize: 10,
                fontName: "Poppins",
              },
              titleTextStyle: {
                fontName: "Poppins",
                fontSize: 11,
              },
            },
          };

          // Inicializar y renderizar el gráfico con un retraso para permitir que el DOM se actualice
          setTimeout(() => {
            try {
              // Crear la tabla de datos y el gráfico
              this.dataTable =
                window.google.visualization.arrayToDataTable(data);
              this.chart = new window.google.visualization[type](chartDiv);

              // Renderizar el gráfico
              this.renderChart();

              // Configurar el observador de redimensionamiento para mayor precisión
              this.setupResizeObserver(container);

              // También escuchar eventos de redimensionamiento de ventana como respaldo
              window.addEventListener("resize", this.handleResize);
            } catch (error) {
              console.error("Error al inicializar el gráfico:", error);
              chartDiv.innerHTML = "Error al cargar el gráfico";
            }
          }, 300);
        }

        // Método para renderizar el gráfico con las opciones actuales
        renderChart = () => {
          if (!this.chart || !this.dataTable) return;

          try {
            this.chart.draw(this.dataTable, this.chartOptions);

            // Optimizar SVG después de renderizar
            setTimeout(() => {
              this.optimizeSvg();
            }, 100);
          } catch (err) {
            console.error("Error al renderizar el gráfico:", err);
          }
        };

        // Mejorar el SVG para responsividad
        optimizeSvg = () => {
          const svgElement = this.querySelector("svg");
          if (svgElement) {
            svgElement.style.width = "100%";
            svgElement.style.height = "100%";
            svgElement.style.maxWidth = "100%";
            svgElement.setAttribute("preserveAspectRatio", "xMidYMid meet");

            // Optimizar el texto para evitar desbordamiento
            const textElements = svgElement.querySelectorAll("text");
            textElements.forEach((text: SVGTextElement) => {
              text.style.fontSize = "10px";
            });
          }
        };

        // Usar ResizeObserver para detección precisa de cambios de tamaño
        setupResizeObserver = (container: HTMLElement) => {
          if (window.ResizeObserver) {
            this.resizeObserver = new window.ResizeObserver((entries: any) => {
              if (entries && entries[0]) {
                this.renderChart();
              }
            });
            this.resizeObserver.observe(container);
          }
        };

        // Método para manejar redimensionamiento de ventana
        handleResize = () => {
          this.renderChart();
        };

        // Limpiar recursos al desconectar
        disconnectedCallback() {
          window.removeEventListener("resize", this.handleResize);

          if (this.resizeObserver) {
            this.resizeObserver.disconnect();
          }

          // Limpiar referencias para evitar pérdidas de memoria
          this.chart = null;
          this.dataTable = null;
        }
      }

      customElements.define("google-chart", GoogleChart);
      console.log("Componente google-chart registrado");
    }
  }, [googleChartsLoaded]);

  // Agregar estilos CSS directamente
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      /* Forzar la responsividad de las gráficas */
      google-chart {
        display: block !important;
        width: 100% !important;
        max-width: 100% !important;
        min-height: 250px !important;
        overflow: hidden !important;
        position: relative !important;
      }
      
      /* Hacer que los SVG sean responsivos */
      google-chart svg {
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
      }
      
      /* Contenedor de mensajes */
      .message-bubble {
        max-width: 100% !important;
        overflow: hidden !important;
        padding: 0 !important;
      }
      
      /* Mejoras para el contenido dentro de burbujas de chat */
      .message-bubble > div {
        width: 100% !important;
        max-width: 100% !important;
        overflow: visible !important;
      }
      
      /* Ajustes específicos para gráficas dentro del chat */
      .message-bubble google-chart {
        margin: 10px 0 !important;
      }
      
      /* Optimizar leyendas y textos */
      .google-visualization-tooltip {
        font-size: 11px !important;
      }
      
      /* Estilos para el componente DeepChat */
      .deep-chat {
        width: 100% !important;
        height: 100% !important;
        border: none !important;
        font-family: "Poppins", sans-serif !important;
      }
      
      /* Ajustar el contenedor de mensajes del DeepChat */
      .deep-chat-messages-container {
        padding: 20px !important;
        font-size: 14px !important;
      }
      
      /* Ajustar el input del DeepChat */
      .deep-chat-input-container {
        padding: 15px !important;
        border-top: 1px solid #eee !important;
      }
      
      /* Mejorar la visualización en pantallas más grandes */
      @media (min-width: 768px) {
        .deep-chat-messages-container {
          font-size: 16px !important;
        }
        
        .message-bubble google-chart {
          margin: 15px 0 !important;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const resetChat = () => {
    conversationIdRef.current = null;
    if (chatRef.current && chatRef.current.clearMessages) {
      chatRef.current.clearMessages();
    }
    setResetKey((prevKey) => prevKey + 1);
    console.log("Chat reiniciado");
  };

  // HTML para el mensaje de bienvenida con la gráfica - usar width="100%" y NO valores fijos!
  const welcomeMessageWithChart = `
    <div style="width:100%; max-width:100%; overflow:hidden;">
      <p style="font-size: 16px; margin-bottom: 15px; font-family: 'Poppins', sans-serif;">¡Hola! Soy el asistente virtual del Banco de Guatemala. ¿En qué puedo ayudarte?</p>
      <div style="margin-top:15px; padding:15px; background-color:white; border-radius:8px; box-shadow:0 1px 4px rgba(0,0,0,0.1); width:100%;">
        <div style="margin-bottom:10px; font-weight:bold; font-size: 16px; font-family: 'Poppins', sans-serif;">Inflación Mensual 2025</div>
        <div style="width:100%; height:280px;">
          <google-chart 
            type="LineChart" 
            style="width:100%; height:100%;" 
            data='[["Mes", "Inflación"], ["Ene", 1.2], ["Feb", 1.3], ["Mar", 1.47], ["Abr", 1.5]]' 
            options='{"colors":["#0088FE"], "legend":"top", "curveType":"function", "vAxis":{"title":"Porcentaje (%)"}, "hAxis":{"title":"Mes"}, "fontName":"Poppins"}'>
          </google-chart>
        </div>
      </div>
    </div>
  `;

  // Datos para otras gráficas - usando también width="100%"
  const pibChartHtml = `
    <div style="width:100%; max-width:100%; overflow:hidden;">
      <div style="margin-top:15px; padding:15px; background-color:white; border-radius:8px; box-shadow:0 1px 4px rgba(0,0,0,0.1); width:100%;">
        <div style="margin-bottom:10px; font-weight:bold; font-size: 16px; font-family: 'Poppins', sans-serif;">PIB por Sector (Miles de millones Q)</div>
        <div style="width:100%; height:280px;">
          <google-chart 
            type="BarChart" 
            style="width:100%; height:100%;" 
            data='[["Sector", "Valor"], ["Agricultura", 52.4], ["Industria", 38.7], ["Servicios", 86.3], ["Comercio", 64.1]]' 
            options='{"colors":["#00C49F"], "legend":"top", "vAxis":{"title":"Sector"}, "hAxis":{"title":"Miles de millones Q"}, "fontName":"Poppins"}'>
          </google-chart>
        </div>
      </div>
    </div>
  `;

  const exportacionesChartHtml = `
    <div style="width:100%; max-width:100%; overflow:hidden;">
      <div style="margin-top:15px; padding:15px; background-color:white; border-radius:8px; box-shadow:0 1px 4px rgba(0,0,0,0.1); width:100%;">
        <div style="margin-bottom:10px; font-weight:bold; font-size: 16px; font-family: 'Poppins', sans-serif;">Distribución de Exportaciones</div>
        <div style="width:100%; height:300px;">
          <google-chart 
            type="PieChart" 
            style="width:100%; height:100%;" 
            data='[["Producto", "Porcentaje"], ["Café", 27.5], ["Azúcar", 18.3], ["Textiles", 22.7], ["Otros", 31.5]]' 
            options='{"colors":["#0088FE", "#00C49F", "#FFBB28", "#FF8042"], "is3D":false, "pieHole":0.4, "fontName":"Poppins"}'>
          </google-chart>
        </div>
      </div>
    </div>
  `;

  const tasaInteresChartHtml = `
    <div style="width:100%; max-width:100%; overflow:hidden;">
      <div style="margin-top:15px; padding:15px; background-color:white; border-radius:8px; box-shadow:0 1px 4px rgba(0,0,0,0.1); width:100%;">
        <div style="margin-bottom:10px; font-weight:bold; font-size: 16px; font-family: 'Poppins', sans-serif;">Tasa de Interés Líder (%)</div>
        <div style="width:100%; height:280px;">
          <google-chart 
            type="LineChart" 
            style="width:100%; height:100%;" 
            data='[["Mes", "Tasa"], ["Nov 24", 7.35], ["Dic 24", 7.45], ["Ene 25", 7.50], ["Feb 25", 7.55], ["Mar 25", 7.65], ["Abr 25", 7.67]]' 
            options='{"colors":["#FF8042"], "legend":"top", "curveType":"function", "vAxis":{"title":"Porcentaje (%)"}, "hAxis":{"title":"Mes"}, "fontName":"Poppins"}'>
          </google-chart>
        </div>
      </div>
    </div>
  `;

  const history = [
    {
      role: "ai",
      html: "Bienvenido a Banguat Chatbot",
    },
  ];

  // Agregar un ID único a cada elemento google-chart para evitar duplicados
  const addUniqueIds = (html: string): string => {
    let counter = 0;
    return html.replace(/<google-chart/g, () => {
      const id = `chart-${Date.now()}-${counter++}`;
      return `<google-chart id="${id}"`;
    });
  };

  // Función para procesar respuestas de la API que contengan marcadores de gráficas
  const processResponse = (response: any) => {
    console.log("----------RESPONSE----------");
    console.log(response);

    // Si la respuesta contiene html con marcadores para gráficas, los reemplazamos
    if (response && response.html && typeof response.html === "string") {
      let processedHtml = response.html;

      // Reemplazar marcadores con los componentes de Google Charts
      if (processedHtml.includes("{{CHART_PIB}}")) {
        processedHtml = processedHtml.replace("{{CHART_PIB}}", pibChartHtml);
      }

      if (processedHtml.includes("{{CHART_EXPORTACIONES}}")) {
        processedHtml = processedHtml.replace(
          "{{CHART_EXPORTACIONES}}",
          exportacionesChartHtml
        );
      }

      if (processedHtml.includes("{{CHART_TASAS}}")) {
        processedHtml = processedHtml.replace(
          "{{CHART_TASAS}}",
          tasaInteresChartHtml
        );
      }

      // Agregar IDs únicos a cada elemento google-chart
      processedHtml = addUniqueIds(processedHtml);

      // Actualizar la respuesta con el HTML procesado
      response.html = processedHtml;
    }

    // Guardar el ID de conversación
    if (response && response.conversation_id) {
      console.log(
        `Nuevo conversation_id para este usuario: ${response.conversation_id}`
      );
      conversationIdRef.current = response.conversation_id;
    }

    console.log("---------------------------");
    return response;
  };

  return (
    <div
      className="App"
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <DeepChat
        key={resetKey}
        ref={chatRef}
        avatars={true}
        connect={{
          url: "https://ngfamyllyh.execute-api.us-east-1.amazonaws.com/dev/PutMessagev2",
        }}
        requestBodyLimits={{ maxMessages: -1 }}
        requestInterceptor={(details: RequestDetails) => {
          console.log("----------REQUEST----------");

          // Extraer solo el último mensaje (mensaje actual)
          if (
            details.body &&
            details.body.messages &&
            Array.isArray(details.body.messages)
          ) {
            const messages = details.body.messages;
            // Obtener solo el último mensaje (el actual)
            if (messages.length > 0) {
              const ultimoMensaje = messages[messages.length - 1];
              // Reemplazar el array completo por solo el último mensaje
              details.body.messages = [ultimoMensaje];
            }
          }

          // Agregar el conversation_id al body si existe
          if (conversationIdRef.current) {
            details.body = {
              ...details.body,
              conversation_id: conversationIdRef.current,
            };
          }
          console.log(details);
          console.log("---------------------------");
          return details;
        }}
        responseInterceptor={processResponse}
        style={{
          borderRadius: "0",
          border: "none",
          width: "100%",
          height: "100%",
          maxWidth: "100%",
          marginBottom: "0",
          paddingBottom: "0",
          backgroundColor: "#ffffff",
          fontFamily: "'Poppins', sans-serif",
        }}
        textInput={{
          placeholder: {
            text: "¿Qué deseas consultar?",
          },
          styles: {
            container: {
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              fontSize: "16px",
            },
          },
        }}
        messageStyles={
          {
            default: {
              text: {
                markdown: true,
              },
              ai: {
                bubble: {
                  backgroundColor: "#f5f5f5",
                  fontSize: "15px",
                  maxWidth: "100%",
                },
              },
              user: {
                bubble: {
                  backgroundColor: "#2a5446",
                  color: "#ffffff",
                  fontSize: "15px",
                  maxWidth: "100%",
                },
              },
            },
          } as any
        }
        history={history}
      />
    </div>
  );
};

// Extender la interfaz Window para TypeScript
declare global {
  interface Window {
    google: any;
    ResizeObserver: any;
  }
}

export default App;
